import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, ChevronRight } from "lucide-react";
import { useI18n } from "@/lib/i18nContext";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/media";
import Seo from "@/components/Seo";

const LOCALE_FOR_LANG = { de: "de-DE", en: "en-GB", fr: "fr-FR" };

export default function BlogListPage() {
  const { t, tl, lang, pathWithLang } = useI18n();
  const [posts, setPosts] = useState([]);
  const [tag, setTag] = useState("");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/blog/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const url = tag ? `/blog?tag=${encodeURIComponent(tag)}` : "/blog";
    api.get(url).then((r) => setPosts(r.data || [])).catch(() => setPosts([]));
  }, [tag]);

  const dateLocale = LOCALE_FOR_LANG[lang] || "de-DE";
  // Show all canonical categories as filter chips (always), regardless of whether any post is tagged with them yet
  const allTags = categories.length > 0
    ? categories
    : Array.from(new Set(posts.flatMap((p) => p.tags || []))).sort();

  return (
    <div data-testid="blog-list-page">
      <Seo title={t("seo.blog.title")} description={t("seo.blog.description")} />
      <section className="relative noise overflow-hidden">
        <div className="absolute inset-0 -z-0">
          <div className="absolute -top-20 right-0 w-[40vw] h-[40vw] rounded-full bg-[var(--gold)] opacity-[0.05] blur-[140px]" />
        </div>
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-12 relative z-10">
          <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-5">
            · {t("blog.heroLabel")}
          </div>
          <h1 className="font-display text-[clamp(2.25rem,5.5vw,4.5rem)] leading-[1.05] tracking-[-0.035em] max-w-5xl">
            {t("blog.heroTitleA")} <span className="font-serif gold-grad">{t("blog.heroTitleAccent")}</span>.
          </h1>
          <p className="mt-8 text-lg text-[var(--text-1)] max-w-3xl leading-relaxed">
            {t("blog.heroIntro")}
          </p>

          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-10">
              <button
                data-testid="filter-all"
                onClick={() => setTag("")}
                className={`tag-pill cursor-pointer ${tag === "" ? "border-[var(--gold)] text-[var(--gold)]" : ""}`}
              >
                {t("blog.filterAll")}
              </button>
              {allTags.map((tg) => (
                <button
                  key={tg}
                  data-testid={`filter-${tg}`}
                  onClick={() => setTag(tg)}
                  className={`tag-pill cursor-pointer ${tag === tg ? "border-[var(--gold)] text-[var(--gold)]" : ""}`}
                >
                  {tg}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {posts.length === 0 ? (
        <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24 text-center">
          <p className="text-[var(--text-2)]">{t("blog.noPosts")}</p>
        </section>
      ) : (
        <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {posts.map((p) => {
              const tr = p.translations?.[lang];
              const title = (tr && tr.title) || tl(p.title) || p.title;
              const excerpt = (tr && tr.excerpt) || tl(p.excerpt) || p.excerpt;
              return (
              <Link
                to={pathWithLang(`/blog/${p.slug}`)}
                key={p.id}
                data-testid={`post-${p.slug}`}
                className="svc-card flex flex-col group p-0 overflow-hidden"
              >
                {p.cover_image && (
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={resolveMediaUrl(p.cover_image)}
                      alt={title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-wider text-[var(--text-2)] mb-3">
                    <Clock size={11} /> {p.reading_minutes} {t("blog.readingTime")}
                    <span className="text-[var(--text-3)]">·</span>
                    {new Date(p.created_at).toLocaleDateString(dateLocale, { day: "2-digit", month: "short", year: "numeric" })}
                  </div>
                  <h3 className="font-display text-xl text-[var(--text-0)] leading-snug group-hover:text-[var(--gold)] transition-colors mb-3">
                    {title}
                  </h3>
                  <p className="text-[var(--text-1)] text-sm leading-relaxed line-clamp-3 mb-4">
                    {excerpt}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {p.tags?.slice(0, 3).map((tg) => (
                      <span key={tg} className="tag-pill">{tg}</span>
                    ))}
                  </div>
                  <div className="mt-auto flex items-center gap-2 text-[var(--gold)] text-xs font-medium uppercase tracking-wider">
                    {t("common.readMore")} <ChevronRight size={12} />
                  </div>
                </div>
              </Link>
            );
            })}
          </div>
        </section>
      )}

      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20">
        <div className="border hairline-gold rounded p-10 lg:p-14 flex flex-col lg:flex-row gap-8 items-center justify-between bg-gradient-to-br from-[var(--bg-1)] to-[var(--bg-0)]">
          <div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight leading-tight max-w-2xl">
              {t("blog.ctaTitleA")} <span className="font-serif gold-grad">{t("blog.ctaTitleAccent")}</span>{t("blog.ctaTitleEnd")}
            </h2>
            <p className="text-[var(--text-1)] mt-3 max-w-xl">
              {t("blog.ctaDesc")}
            </p>
          </div>
          <Link to={pathWithLang("/kontakt")} className="btn-primary" data-testid="blog-list-cta">
            {t("blog.ctaButton")} <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
